/**
 * Mobile Bottom Navigation - Tab bar that appears on small screens.
 * Mirrors the pattern used in native mobile apps.
 */

import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import {
  FiHome,
  FiFileText,
  FiDollarSign,
  FiClipboard,
  FiSettings,
} from 'react-icons/fi';
import './MobileBottomNav.css';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface MobileBottomNavProps {
  isAdmin: boolean;
}

const userNavItems: NavItem[] = [
  { label: 'Home', path: '/dashboard', icon: <FiHome /> },
  { label: 'Docs', path: '/dashboard/dms', icon: <FiFileText /> },
  { label: 'Books', path: '/dashboard/bookkeeping', icon: <FiDollarSign /> },
  { label: 'Tax', path: '/dashboard/tax-filing', icon: <FiClipboard /> },
  { label: 'More', path: '/dashboard/settings', icon: <FiSettings /> },
];

const adminNavItems: NavItem[] = [
  { label: 'Home', path: '/admin', icon: <FiHome /> },
  { label: 'Docs', path: '/admin/dms', icon: <FiFileText /> },
  { label: 'Books', path: '/admin/bookkeeping', icon: <FiDollarSign /> },
  { label: 'Tax', path: '/admin/tax-filing', icon: <FiClipboard /> },
  { label: 'More', path: '/admin/settings', icon: <FiSettings /> },
];

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isAdmin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const items = isAdmin ? adminNavItems : userNavItems;

  // Guard-then-land: block default nav, run the unsaved-changes guard, then navigate.
  const guardedGo = useCallback((to: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    void (async () => {
      if (!(await confirmDiscardChanges())) return;
      setFormDirty(false);
      navigate(to);
    })();
  }, [navigate]);

  return (
    <nav className="mobile-bottom-nav">
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={guardedGo(item.path)}
          className={`bottom-nav-item ${location.pathname === item.path ? 'active' : ''}`}
        >
          <span className="bottom-nav-icon">{item.icon}</span>
          <span className="bottom-nav-label">{item.label}</span>
        </Link>
      ))}
    </nav>
  );
};

export default MobileBottomNav;
